import React, { useMemo, useState } from 'react';
import { Copy, CheckCircle, AlertTriangle, XCircle, Clock, Zap } from 'lucide-react';
import { cpFetch } from '@/lib/http';
import { Link } from 'react-router-dom';
import { deriveServiceHealth } from '@/lib/serviceHealth';
import {
  formatHealthCacheLine,
  formatHealthDetailsLines,
  formatHealthLatencyMs,
  renderHealthDetailsJson,
  sanitizeHealthDetails,
} from '@/lib/healthDetails';

type ServiceStatus = 'ok' | 'degraded' | 'down';

type EnhancedStatusComponent = {
  status: ServiceStatus;
  ok: boolean;
  latency_ms: number;
  status_code: number;
  observed_at?: string;
  mode?: string | null;
  reason?: string | null;
  freshness_sec?: number | null;
  last_event_ts?: string | null;
  details?: Record<string, any>;
  metrics?: Record<string, any>;
};

type EnhancedStatusPayload = {
  ok: boolean;
  updated_at: string;
  cache?: {
    ttl_ms?: number;
    age_ms?: number;
    fresh?: boolean;
  };
  summary?: {
    total: number;
    ok: number;
    degraded: number;
    down: number;
  };
  components: Record<string, EnhancedStatusComponent>;
};

type Props = {
  data?: EnhancedStatusPayload | null;
  isLoading?: boolean;
  error?: string | null;
};

const statusConfig: Record<ServiceStatus, { color: string; icon: React.ComponentType; bgColor: string }> = {
  ok: { color: 'text-green-700', icon: CheckCircle, bgColor: 'bg-green-50 border-green-200' },
  degraded: { color: 'text-amber-700', icon: AlertTriangle, bgColor: 'bg-amber-50 border-amber-200' },
  down: { color: 'text-red-700', icon: XCircle, bgColor: 'bg-red-50 border-red-200' }
};

function ServiceTile({ 
  serviceKey, 
  service, 
  onCopyReport,
  onOpen,
}: { 
  serviceKey: string; 
  service: EnhancedStatusComponent; 
  onCopyReport: (serviceKey: string) => Promise<void>;
  onOpen: () => void;
}) {
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const detailLines = formatHealthDetailsLines(service.details, 3);
  const metricLines = formatHealthDetailsLines(service.metrics, 2);
  
  const config = statusConfig[service.status];
  const StatusIcon = config.icon as React.ComponentType<{ className?: string }>;
  
  const handleCopyReport = async () => {
    if (copying) return;
    
    setCopying(true);
    try {
      await onCopyReport(serviceKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy report:', error);
    } finally {
      setCopying(false);
    }
  };
  
  const formatServiceName = (key: string) => {
    const names: Record<string, string> = {
      kong: 'Kong API Gateway',
      pulsar: 'Pulsar Message Broker',
      mosquitto: 'Mosquitto MQTT',
      mongodb: 'MongoDB Atlas',
      'mongo-platform': 'Mongo — Platform',
      'mongo-contextualizer': 'Mongo — Contextualizer',
      'mongo-agents': 'Mongo — Agents',
      'mongo-pim': 'Mongo — PIM',
      'mongo-instances': 'Mongo — Instances',
      timescaledb: 'TimescaleDB',
      redis: 'Redis',
      nginx: 'Nginx Web Server',
      iot_ingestor: 'A4 IoT Ingestor',
      contextualizer: 'A7 Contextualizer',
      telemetry_read: 'A12 Telemetry Reader'
    };
    if (names[key]) return names[key];
    if (key.startsWith('mongo-')) return 'Mongo — ' + key.slice(6).charAt(0).toUpperCase() + key.slice(7);
    return key.charAt(0).toUpperCase() + key.slice(1);
  };
  
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      className={`rounded-xl border-2 ${config.bgColor} shadow-sm hover:shadow-md transition-all duration-200 p-4 space-y-3 cursor-pointer`}
      title="Click for details"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-5 h-5 ${config.color}`} />
          <h3 className="text-sm font-semibold text-gray-900">
            {formatServiceName(serviceKey)}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
            service.status === 'ok' ? 'bg-green-100 text-green-700' :
            service.status === 'degraded' ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            {service.status.toUpperCase()}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); handleCopyReport(); }}
            disabled={copying}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            title="Copy diagnostic report"
          >
            {copied ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : copying ? (
              <Clock className="w-4 h-4 text-gray-400 animate-spin" />
            ) : (
              <Copy className="w-4 h-4 text-gray-500 hover:text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Status Info */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1">
          <Zap className="w-3 h-3 text-gray-400" />
          <span className="text-gray-600">
            {formatHealthLatencyMs(service.latency_ms)}
          </span>
        </div>
        <div className="text-right">
          <span className={`font-medium ${
            service.status_code >= 200 && service.status_code < 300 ? 'text-green-600' :
            service.status_code >= 400 ? 'text-red-600' : 'text-amber-600'
          }`}>
            HTTP {service.status_code}
          </span>
        </div>
      </div>

      {/* Error Message */}
      {service.reason && (
        <div className="p-2 bg-red-50 border border-red-200 rounded-md">
          <p className="text-xs text-red-700 font-medium">Error:</p>
          <p className="text-xs text-red-600">{service.reason}</p>
        </div>
      )}

      {/* Last Event / Freshness */}
      {service.last_event_ts && (
        <div className="text-xs text-gray-500">
          <span className="font-medium">Last up:</span> {new Date(service.last_event_ts).toLocaleString()}
          {service.freshness_sec && (
            <span className="ml-2">({formatDuration(service.freshness_sec)} ago)</span>
          )}
        </div>
      )}

      {/* Service Details — sanitized; never dump accessors/tokens */}
      {detailLines.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-700">Details:</p>
          <div className="grid grid-cols-1 gap-1">
            {detailLines.map((line) => (
              <div key={line.key} className="flex justify-between text-xs">
                <span className="text-gray-500 capitalize">{line.label}:</span>
                <span className="text-gray-700 font-medium truncate ml-2">
                  {line.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metrics */}
      {metricLines.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-700">Metrics:</p>
          <div className="grid grid-cols-1 gap-1">
            {metricLines.map((line) => (
              <div key={line.key} className="flex justify-between text-xs">
                <span className="text-gray-500 capitalize">{line.label}:</span>
                <span className="text-gray-700 font-medium">{line.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timestamp */}
      <div className="text-xs text-gray-400 border-t pt-2">
        Checked {service.observed_at ? new Date(service.observed_at).toLocaleTimeString() : 'now'}
      </div>
    </div>
  );
}

function Badge({ 
  label, 
  value, 
  color 
}: { 
  label: string; 
  value: number; 
  color: string;
}) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 ${color}`}>
      <span className="uppercase tracking-wide text-[10px] font-semibold">{label}</span>
      <span className="text-sm font-bold">{value}</span>
    </span>
  );
}

type ServiceCategory = {
  label: string;
  keys: Set<string>;
  prefixes?: string[];
};

const SERVICE_CATEGORIES: ServiceCategory[] = [
  { label: 'Databases', keys: new Set(['mongodb', 'timescaledb', 'redis']), prefixes: ['mongo-'] },
  { label: 'Infrastructure', keys: new Set(['kong', 'pulsar', 'mosquitto', 'vault', 'nginx', 'farmer-portal']) },
  { label: 'Core Platform', keys: new Set(['orchestrator', 'provision-jobs-worker', 'volcano-engine', 'agent-registry', 'agent-creator', 'interpreter']) },
  { label: 'Data Pipeline', keys: new Set(['api-manager', 'dataflow-manager', 'dam-processor', 'telemetry-consumer', 'shadow-replicator', 'telemetry-read']) },
  { label: 'IoT', keys: new Set(['iot-ingestor', 'iot-shack']) },
  { label: 'App Services', keys: new Set(['asset-app-api', 'mapper-app-api']) },
];

function matchesCategory(key: string, cat: ServiceCategory): boolean {
  if (cat.keys.has(key)) return true;
  if (cat.prefixes) {
    for (const p of cat.prefixes) {
      if (key.startsWith(p)) return true;
    }
  }
  return false;
}

function categorizeServices(components: Record<string, EnhancedStatusComponent>) {
  const categorized = SERVICE_CATEGORIES.map(cat => ({
    label: cat.label,
    entries: [] as [string, EnhancedStatusComponent][],
  }));
  const uncategorized: [string, EnhancedStatusComponent][] = [];

  for (const [key, svc] of Object.entries(components)) {
    let placed = false;
    for (let i = 0; i < SERVICE_CATEGORIES.length; i++) {
      if (matchesCategory(key, SERVICE_CATEGORIES[i])) {
        categorized[i].entries.push([key, svc]);
        placed = true;
        break;
      }
    }
    if (!placed) uncategorized.push([key, svc]);
  }

  if (uncategorized.length > 0) {
    categorized.push({ label: 'Other', entries: uncategorized });
  }

  return categorized.filter(cat => cat.entries.length > 0);
}

export default function EnhancedStatusTiles({ data, isLoading, error }: Props) {
  const [openSvc, setOpenSvc] = useState<{ key: string; service: EnhancedStatusComponent } | null>(null);

  const openTitle = useMemo(() => {
    if (!openSvc) return '';
    if (openSvc.key === 'pulsar') return 'Pulsar Message Broker';
    return openSvc.key;
  }, [openSvc]);

  const categories = useMemo(() => {
    if (!data?.components) return [];
    return categorizeServices(data.components);
  }, [data?.components]);

  const copyServiceReport = async (serviceKey: string) => {
    try {
      const { report } = await cpFetch<{ report: string }>(`/services/${encodeURIComponent(serviceKey)}/report`);
      await navigator.clipboard.writeText(report);
    } catch (error) {
      console.error('Failed to copy report:', error);
      const service = data?.components[serviceKey];
      if (service) {
        const basicReport = [
          `=== ${serviceKey.toUpperCase()} Service Report ===`,
          `Status: ${service.status}`,
          `Latency: ${service.latency_ms}ms`,
          `HTTP Code: ${service.status_code}`,
          service.reason ? `Error: ${service.reason}` : '',
          `Checked: ${service.observed_at || 'now'}`
        ].filter(Boolean).join('\n');
        
        await navigator.clipboard.writeText(basicReport);
      }
    }
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Status monitoring error:</span>
        </div>
        <p className="text-sm text-red-600 mt-1">{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Clock className="w-5 h-5 animate-spin" />
        <span>Loading enhanced platform status…</span>
      </div>
    );
  }

  if (!data || !data.components || Object.keys(data.components).length === 0) {
    return (
      <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">
        No status data available. Health monitoring may be starting up.
      </div>
    );
  }

  const summary = deriveServiceHealth(data);
  const cacheLine = formatHealthCacheLine(data.cache);

  return (
    <div className="space-y-6">
      {/* Summary Badges — same down-count derivation as header / Overview */}
      {summary.total > 0 && (
        <div className="flex flex-wrap gap-3 text-xs">
          <Badge label="Total" value={summary.total} color="bg-gray-100 text-gray-700" />
          <Badge label="Healthy" value={summary.ok} color="bg-green-100 text-green-700" />
          <Badge label="Degraded" value={summary.degraded} color="bg-amber-100 text-amber-700" />
          <Badge label="Down" value={summary.down} color="bg-red-100 text-red-700" />
        </div>
      )}

      {/* Categorized Service Tiles */}
      {categories.map(cat => (
        <div key={cat.label}>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{cat.label}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {cat.entries.map(([serviceKey, service]) => (
              <ServiceTile
                key={serviceKey}
                serviceKey={serviceKey}
                service={service}
                onCopyReport={copyServiceReport}
                onOpen={() => setOpenSvc({ key: serviceKey, service })}
              />
            ))}
          </div>
        </div>
      ))}

      {openSvc && (
        <ServiceDetailModal
          title={openTitle}
          serviceKey={openSvc.key}
          service={openSvc.service}
          onClose={() => setOpenSvc(null)}
        />
      )}

      {/* Footer Info */}
      <div className="text-xs text-gray-400 border-t pt-3 flex justify-between items-center">
        <div className="flex gap-4">
          <span>Updated {new Date(data.updated_at).toLocaleString()}</span>
          {cacheLine ? <span>{cacheLine}</span> : null}
        </div>
      </div>
    </div>
  );
}

export type { EnhancedStatusPayload, EnhancedStatusComponent };

function ServiceDetailModal({
  title,
  serviceKey,
  service,
  onClose,
}: {
  title: string;
  serviceKey: string;
  service: EnhancedStatusComponent;
  onClose: () => void;
}) {
  const isPulsar = serviceKey === 'pulsar';
  const details = (sanitizeHealthDetails(service.details || {}) || {}) as Record<string, any>;

  const now = Date.now();
  const sinceIso = new Date(now - 24 * 60 * 60 * 1000).toISOString();
  const untilIso = new Date(now).toISOString();
  const provisioningEventsHref =
    `/ops/mcp-events?agent_id=${encodeURIComponent('app-provisioner')}` +
    `&workflow=${encodeURIComponent('provision_app')}` +
    `&since=${encodeURIComponent(sinceIso)}` +
    `&until=${encodeURIComponent(untilIso)}` +
    `&auto=1`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/30" onMouseDown={onClose}>
      <div
        className="w-full max-w-4xl bg-white rounded-xl shadow-xl border border-gray-200 p-4 md:p-6 space-y-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-lg font-semibold text-gray-900 truncate">{title}</div>
            <div className="text-xs text-gray-500 mt-1">
              Status: <span className="font-medium text-gray-800">{service.status}</span>
              {service.reason ? <span className="ml-2 text-red-600">({service.reason})</span> : null}
            </div>
          </div>
          <button className="text-sm border rounded px-3 py-1" onClick={onClose}>
            Close
          </button>
        </div>

        {isPulsar ? (
          <PulsarDetail details={details} />
        ) : (
          <div className="rounded-lg border bg-gray-50 p-3 text-xs">
            <div className="font-medium text-gray-700 mb-2">Details</div>
            <pre className="overflow-auto whitespace-pre-wrap break-words">{renderHealthDetailsJson(details)}</pre>
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-3">
          <div className="text-xs text-gray-500">
            Tip: use the copy-report button on the tile for a full diagnostic report.
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={provisioningEventsHref}
              className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
              title="Jump to provisioning workflow events (Timescale)"
            >
              Provisioning events
            </Link>
            <Link
              to="/ops/mcp-events"
              className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
              title="Search agent/MCP events persisted from Pulsar into Timescale"
            >
              Agent conversations
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function PulsarDetail({ details }: { details: Record<string, any> }) {
  const topics = details?.topics || {};
  const byKind = topics?.by_kind && typeof topics.by_kind === 'object' ? topics.by_kind : {};
  const tenants: string[] = Array.isArray(topics?.tenants) ? topics.tenants : [];
  const usageHints = details?.usage_hints && typeof details.usage_hints === 'object' ? details.usage_hints : {};
  const statsSample: any[] = Array.isArray(topics?.stats_sample) ? topics.stats_sample : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <Info label="Admin base" value={details?.http_base || '—'} />
        <Info label="Namespace" value={details?.namespace || '—'} />
        <Info label="Topics" value={String(topics?.total ?? '—')} />
      </div>

      {details?.health_body ? (
        <div className="rounded-lg border bg-red-50 p-3 text-xs">
          <div className="font-medium text-red-700 mb-2">Health response body</div>
          <pre className="overflow-auto whitespace-pre-wrap break-words">{String(details.health_body)}</pre>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg border p-3">
          <div className="text-sm font-medium text-gray-900 mb-2">Tenants (from topic suffix)</div>
          <div className="flex flex-wrap gap-2">
            {tenants.length ? tenants.map((t) => (
              <span key={t} className="text-xs rounded-full bg-gray-100 px-2 py-1">{t}</span>
            )) : <span className="text-xs text-gray-500">No tenant-suffixed topics detected.</span>}
          </div>
        </div>

        <div className="rounded-lg border p-3">
          <div className="text-sm font-medium text-gray-900 mb-2">Topic kinds</div>
          <div className="text-xs text-gray-700 space-y-1">
            {Object.keys(byKind).length ? Object.entries(byKind).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="font-mono">{k}</span>
                <span>{String(v)}</span>
              </div>
            )) : <div className="text-gray-500">No topic kind breakdown available.</div>}
          </div>
        </div>
      </div>

      <div className="rounded-lg border p-3">
        <div className="text-sm font-medium text-gray-900 mb-2">Who uses Pulsar (by topic kind)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          {Object.entries(usageHints).map(([kind, hint]: any) => (
            <div key={kind} className="rounded border bg-gray-50 p-2">
              <div className="font-mono text-gray-800">{kind}</div>
              {hint?.purpose ? <div className="text-gray-600 mt-1">{hint.purpose}</div> : null}
              {Array.isArray(hint?.producers) && hint.producers.length ? (
                <div className="mt-2"><span className="font-medium">Producers:</span> {hint.producers.join(', ')}</div>
              ) : null}
              {Array.isArray(hint?.consumers) && hint.consumers.length ? (
                <div className="mt-1"><span className="font-medium">Consumers:</span> {hint.consumers.join(', ')}</div>
              ) : null}
            </div>
          ))}
          {!Object.keys(usageHints).length ? <div className="text-gray-500">No usage hints available.</div> : null}
        </div>
      </div>

      {statsSample.length ? (
        <div className="rounded-lg border p-3">
          <div className="text-sm font-medium text-gray-900 mb-2">Live stats sample</div>
          <div className="overflow-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 pr-3">topic</th>
                  <th className="py-1 pr-3">tenant</th>
                  <th className="py-1 pr-3">kind</th>
                  <th className="py-1 pr-3">in</th>
                  <th className="py-1 pr-3">out</th>
                  <th className="py-1 pr-3">producers</th>
                  <th className="py-1 pr-3">subs</th>
                  <th className="py-1 pr-3">backlog</th>
                </tr>
              </thead>
              <tbody>
                {statsSample.map((row, idx) => {
                  const subs = Array.isArray(row?.subscriptions) ? row.subscriptions : [];
                  const backlog = subs.reduce((sum: number, s: any) => sum + (typeof s?.msg_backlog === 'number' ? s.msg_backlog : 0), 0);
                  return (
                    <tr key={idx} className="border-t">
                      <td className="py-1 pr-3 font-mono max-w-[320px] truncate" title={String(row?.name || row?.topic || '')}>
                        {String(row?.name || row?.topic || '—')}
                      </td>
                      <td className="py-1 pr-3">{row?.tenant_id || '—'}</td>
                      <td className="py-1 pr-3 font-mono">{row?.kind || '—'}</td>
                      <td className="py-1 pr-3">{row?.msg_rate_in ?? '—'}</td>
                      <td className="py-1 pr-3">{row?.msg_rate_out ?? '—'}</td>
                      <td className="py-1 pr-3">{row?.producers ?? '—'}</td>
                      <td className="py-1 pr-3">{subs.length}</td>
                      <td className="py-1 pr-3">{backlog}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-mono text-gray-900 break-all">{value}</div>
    </div>
  );
}
